import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import { ModalShell } from '../shared';
import type { UnsyncedOrder } from '../../../../types/api.types';
import { getOrderHeaderId } from './unsyncedOrderUtils';

interface ReassignUnsyncedOrdersDialogProps {
    isOpen: boolean;
    selectedOrders: UnsyncedOrder[];
    newStoreId: string;
    newStoreBrandId: string;
    isLoading: boolean;
    onNewStoreIdChange: (value: string) => void;
    onNewStoreBrandIdChange: (value: string) => void;
    onConfirm: () => void;
    onClose: () => void;
}

export default function ReassignUnsyncedOrdersDialog({
    isOpen,
    selectedOrders,
    newStoreId,
    newStoreBrandId,
    isLoading,
    onNewStoreIdChange,
    onNewStoreBrandIdChange,
    onConfirm,
    onClose,
}: ReassignUnsyncedOrdersDialogProps) {
    const selectedCount = selectedOrders.length;
    const previewOrders = selectedOrders.slice(0, 3);
    const remainingCount = selectedCount - previewOrders.length;

    const footer = (
        <>
            <Button
                variant="secondary"
                className="flex-1"
                onClick={onClose}
                disabled={isLoading}
            >
                Cancel
            </Button>
            <Button
                variant="primary"
                className="flex-1"
                isLoading={isLoading}
                onClick={onConfirm}
            >
                Move orders
            </Button>
        </>
    );

    return (
        <ModalShell
            isOpen={isOpen}
            title={`Move ${selectedCount} order${selectedCount !== 1 ? 's' : ''} to another store`}
            subtitle="Reassign failed orders so they sync under a different store."
            onClose={onClose}
            footer={footer}
            maxWidth="sm"
            elevation="nested"
        >
            <div className="space-y-4">
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-[#94a3b8] mb-2">
                        Selected orders
                    </p>
                    <ul className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] divide-y divide-[#e2e8f0]">
                        {previewOrders.map((order) => (
                            <li
                                key={order._id}
                                className="px-3 py-2 text-sm text-[#1e293b] truncate"
                                title={getOrderHeaderId(order)}
                            >
                                {getOrderHeaderId(order)}
                            </li>
                        ))}
                        {remainingCount > 0 && (
                            <li className="px-3 py-2 text-sm text-[#64748b]">
                                and {remainingCount} more
                            </li>
                        )}
                    </ul>
                </div>

                <Input
                    label="New store ID (optional)"
                    placeholder="Enter the destination store ID"
                    value={newStoreId}
                    onChange={(e) => onNewStoreIdChange(e.target.value)}
                />

                <Input
                    label="New brand ID (optional)"
                    placeholder="Enter the destination brand ID"
                    value={newStoreBrandId}
                    onChange={(e) => onNewStoreBrandIdChange(e.target.value)}
                />
            </div>
        </ModalShell>
    );
}
